import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Badge, Row, Col, Spinner, Alert, Pagination } from 'react-bootstrap';
import VereinbarungService from '../services/vereinbarung.service';

// HIER LEGEN SIE IHRE FARBEN FEST.
// Diese Konstante steht jetzt außerhalb der Komponente für eine bessere Übersicht.
const categoryColorMap = {
    't_users': '#e6f0ff',           // Hellblau
    't_ad': '#e2f0d9',              // Hellgrün
    't_batch_codes': '#fff2cc',     // Gold
    't_invoices': '#f8d7da',        // Rot
    // Fügen Sie hier weitere Tabellen und Ihre Wunschfarben hinzu
    'default': '#e9ecef'            // Standard-Grau
};

const PlatzhalterAuswahlModal = ({ show, onHide, onSelectPlaceholder }) => {
    // === State Management ===
    const [placeholders, setPlaceholders] = useState([]);
    const [categories, setCategories] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [hoveredItem, setHoveredItem] = useState(null);

    // Effekt für die Suche (mit Debounce)
    useEffect(() => {
        const timerId = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
            setCurrentPage(1);
        }, 500);
        return () => clearTimeout(timerId);
    }, [searchTerm]);

    // Effekt zum Laden der Platzhalter
    useEffect(() => {
        if (show) {
            setLoading(true);
            setError('');
            const params = { page: currentPage, size: 10, search: debouncedSearchTerm, category: selectedCategory };
            VereinbarungService.getAllPlaceholders(params)
                .then(response => {
                    setPlaceholders(response.data.placeholders);
                    setTotalPages(response.data.totalPages);
                })
                .catch(err => setError('Platzhalter konnten nicht geladen werden.'))
                .finally(() => setLoading(false));
        }
    }, [show, currentPage, debouncedSearchTerm, selectedCategory]);

    // Effekt zum Laden der Kategorien
    useEffect(() => {
        if (show) {
            VereinbarungService.getPlaceholderCategories()
                .then(response => setCategories(response.data))
                .catch(err => console.error("Fehler beim Laden der Kategorien:", err));
        }
    }, [show]);

    // === Hilfsfunktionen ===
    const handleSelectItem = (key) => {
        onSelectPlaceholder(key);
        onHide();
    };
    
    const handleCategoryClick = (category) => {
        setSelectedCategory(prev => prev === category ? '' : category);
        setCurrentPage(1);
    };

    // === Styles ===
    const styles = {
        modalHeader: { background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: '15px 15px 0 0', border: 'none', padding: '25px 30px' },
        modalTitle: { color: '#ffffff', fontSize: '24px', fontWeight: '600' },
        modalBody: { padding: '30px', maxHeight: '600px', overflowY: 'auto', background: '#f8f9fa' },
        searchInput: { borderRadius: '12px', border: '2px solid #e2e8f0', padding: '12px 20px', fontSize: '15px', backgroundColor: '#ffffff' },
        categoryContainer: { margin: '25px 0', display: 'flex', gap: '10px', flexWrap: 'wrap' },
        categoryButton: { borderRadius: '10px', padding: '8px 16px', fontSize: '14px', fontWeight: '500', border: '2px solid #e2e8f0', transition: 'all 0.3s ease', cursor: 'pointer', background: '#ffffff', color: '#4a5568' },
        categoryButtonActive: { background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#ffffff', border: '2px solid transparent' },
        platzhalterItem: { padding: '15px', borderRadius: '12px', border: '2px solid #e2e8f0', cursor: 'pointer', transition: 'all 0.3s ease', backgroundColor: '#ffffff', marginBottom: '12px' },
        platzhalterItemHover: { transform: 'translateX(5px)', borderColor: '#667eea', backgroundColor: '#f4f7ff', boxShadow: '0 4px 15px rgba(102, 126, 234, 0.15)' },
        platzhalterKey: { fontFamily: 'monospace', fontSize: '13px', padding: '4px 8px', borderRadius: '6px', marginRight: '10px', display: 'inline-block', color: '#495057' },
        platzhalterDescription: { fontSize: '13px', color: '#718096', marginTop: '5px' },
        noResults: { textAlign: 'center', padding: '40px', color: '#a0aec0' },
        noResultsIcon: { fontSize: '48px', marginBottom: '15px' },
        modalFooter: { borderTop: '1px solid #dee2e6', padding: '20px 30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
        closeButton: { background: 'linear-gradient(135deg, #6c757d 0%, #343a40 100%)', border: 'none', borderRadius: '10px', padding: '10px 30px', fontSize: '15px', fontWeight: '500' }
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header style={styles.modalHeader} closeButton>
                <Modal.Title style={styles.modalTitle}>🏷️ Platzhalter auswählen</Modal.Title>
            </Modal.Header>
            
            <Modal.Body style={styles.modalBody}>
                <Form.Control type="text" placeholder="Suchen nach Schlüssel oder Beschreibung..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={styles.searchInput} />
                <div style={styles.categoryContainer}>
                    {categories.map(cat => (
                        <button key={cat} onClick={() => handleCategoryClick(cat)} style={{ ...styles.categoryButton, ...(selectedCategory === cat && styles.categoryButtonActive) }}>
                            {cat}
                        </button>
                    ))}
                </div>
                {loading && <div className="text-center p-5"><Spinner animation="border" variant="primary" /></div>}
                {error && <Alert variant="danger">{error}</Alert>}
                {!loading && !error && (
                    placeholders.length > 0 ? (
                        <div>
                            {placeholders.map((item, index) => (
                                <div key={item.platzhalter_id} style={{ ...styles.platzhalterItem, ...(hoveredItem === index ? styles.platzhalterItemHover : {}) }} onMouseEnter={() => setHoveredItem(index)} onMouseLeave={() => setHoveredItem(null)} onClick={() => handleSelectItem(item.platzhalter_schluessel)}>
                                    <div style={styles.platzhalterLabel}>
                                        <Badge style={{ ...styles.platzhalterKey, backgroundColor: categoryColorMap[item.quell_tabelle] || categoryColorMap['default'] }}>
                                            {item.platzhalter_schluessel}
                                        </Badge>
                                    </div>
                                    <div style={styles.platzhalterDescription}>{item.beschreibung}</div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div style={styles.noResults}>
                            <div style={styles.noResultsIcon}>🤷</div>
                            <h5>Keine Platzhalter gefunden</h5>
                            <p>Bitte versuchen Sie es mit anderen Filtern.</p>
                        </div>
                    )
                )}
            </Modal.Body>
            
            <Modal.Footer style={styles.modalFooter}>
                {totalPages > 1 && (
                    <Pagination>
                        <Pagination.Prev disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)} />
                        {currentPage > 2 && (<>
                            <Pagination.Item onClick={() => setCurrentPage(1)}>1</Pagination.Item>
                            {currentPage > 3 && <Pagination.Ellipsis disabled />}
                        </>)}
                        {[...Array(totalPages).keys()].map(number => {
                            const pageNumber = number + 1;
                            if (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1 && pageNumber > 0 && pageNumber <= totalPages) {
                                return (<Pagination.Item key={pageNumber} active={pageNumber === currentPage} onClick={() => setCurrentPage(pageNumber)}>{pageNumber}</Pagination.Item>);
                            }
                            return null;
                        })}
                        {currentPage < totalPages - 1 && (<>
                            {currentPage < totalPages - 2 && <Pagination.Ellipsis disabled />}
                            <Pagination.Item onClick={() => setCurrentPage(totalPages)}>{totalPages}</Pagination.Item>
                        </>)}
                        <Pagination.Next disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)} />
                    </Pagination>
                )}
                <Button variant="secondary" onClick={onHide} style={styles.closeButton}>Schließen</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default PlatzhalterAuswahlModal;